import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns/format";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { asTitle } from "utils/Utils";

import { Criteria } from "trajectory/TrajectoryService";

export type HistoryDialogProps = {
	competence: Criteria;
	editable: boolean;

	getField: (
		prop: string,
		name: string,
		disabled: boolean,
		update: (name: string, value: string) => void
	) => JSX.Element;
	updateField: (date: string, name: string, value: string) => void;
	deleteEvaluation: (date: string) => void;
	onClose: () => void;
};

const rowStyle = {
	"& > *": {
		borderBottom: "unset"
	}
};

const HistoryDialog: React.FC<HistoryDialogProps> = (
	props: HistoryDialogProps
) => {
	const { t } = useTranslation();

	const [edit, setEdit] = useState(false);

	// This is just to cause a re-render
	const [changes, setChanges] = useState(0);

	function remove(element: string): void {
		props.deleteEvaluation(element);
		setChanges(changes + 1);
	}

	function update(element: string, name: string, value: string): void {
		props.updateField(element, name, value);
		setChanges(changes + 1);
	}

	return (
		<Dialog onClose={props.onClose} fullWidth open={true}>
			<DialogTitle>
				{asTitle(t("historyTitle"))}
				{props.editable && !edit && (
					<IconButton
						aria-label="edit"
						onClick={() => setEdit(true)}
						sx={{
							position: "absolute",
							right: 8,
							top: 8
						}}>
						<EditIcon />
					</IconButton>
				)}
			</DialogTitle>
			{Object.entries(props.competence.properties).map(element => (
				<Table size="small" key={element[0]}>
					<TableHead>
						<TableRow>
							<TableCell>
								<Grid
									container
									alignItems="flex-end"
									sx={{ minHeight: 40 }}>
									<Grid item flexGrow={1}>
										{format(
											new Date(element[0]),
											"dd/MM/yyyy HH:mm:ss"
										)}
									</Grid>
									<Grid item>
										{edit && (
											<IconButton
												onClick={() => remove(element[0])}>
												<DeleteIcon />
											</IconButton>
										)}
									</Grid>
								</Grid>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{Object.entries(element[1]).map(property => {
							const key = property[0];
							const value = property[1];
							return (
								<TableRow key={key} sx={rowStyle}>
									<TableCell>
										{props.getField(
											key,
											value,
											!props.editable || !edit,
											(name, value) =>
												update(element[0], name, value)
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			))}
		</Dialog>
	);
};

export default HistoryDialog;
